import { useMemo, useState } from 'react'
import { Button } from '../../lib/shadcn/button'
import { cn } from '../../lib/shadcn/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Check, CheckCheck, PackageCheck, PackageOpen, Printer, X } from 'lucide-react'

// Flavor-first picking: scan a bag of a flavor, the Hub lists every open order
// (tote) that still needs it, and scanning that tote's QR (or the order label)
// packs one bag. Each tote gets a numbered bin for the pick run (#197) and
// totes are listed by bin number, so the shelf can match.

export type PickMode = 'order' | 'flavor'

const PICK_MODE_KEY = 'poppinHub.scanPickMode'

export function loadPickMode(): PickMode {
  try {
    return window.localStorage.getItem(PICK_MODE_KEY) === 'flavor' ? 'flavor' : 'order'
  } catch {
    return 'order'
  }
}

export function savePickMode(mode: PickMode) {
  try {
    window.localStorage.setItem(PICK_MODE_KEY, mode)
  } catch {
    // Private window or blocked storage: the mode just isn't remembered.
  }
}

export interface FlavorPickLine {
  id: number
  order_id: string
  item_name: string
  item_sku: string
  quantity_ordered: number
  quantity_picked: number
  quantity_printed?: number
  /** When the order was marked Ready to pick (null = printed but held). */
  ready_to_pick_at?: string | null
  customer?: string
  contact_number?: string
  /** The customer name printed on tote labels. */
  tote_name?: string | null
  order_status?: string
  /** Web store / Faire order under a shared customer: its own tote. */
  catch_all?: boolean
  /** Numbered bin for this pick run (orders.pick_bin); null until given one. */
  pick_bin?: number | null
  /** Zoho customer id (customers.customer_id), for printing a tote label. */
  customer_id?: string
}

/** Customer name as printed on the tote label, falling back to the order's. */
export function toteName(l: { tote_name?: string | null; customer?: string }): string {
  return (l.tote_name ?? '').trim() || (l.customer ?? '').trim()
}

/** A to Z by tote name, then order number. */
export function byCustomer(
  a: { tote_name?: string | null; customer?: string; order_id: string },
  b: { tote_name?: string | null; customer?: string; order_id: string },
): number {
  return (
    toteName(a).localeCompare(toteName(b), undefined, { sensitivity: 'base' }) ||
    String(a.order_id).localeCompare(String(b.order_id))
  )
}

/** The bin number on a line, or null when it doesn't have one yet. */
export function binOf(l: { pick_bin?: number | null }): number | null {
  const n = Number(l.pick_bin ?? 0)
  return Number.isInteger(n) && n > 0 ? n : null
}

/** Lowest bin among a tote's lines (a customer with two orders can have two). */
export function lowestBin(ls: Array<{ pick_bin?: number | null }>): number | null {
  let best: number | null = null
  for (const l of ls) {
    const n = binOf(l)
    if (n !== null && (best === null || n < best)) best = n
  }
  return best
}

/** By bin number (ones without a bin yet last), then A to Z. */
export function byBin(
  a: { pick_bin?: number | null; tote_name?: string | null; customer?: string; order_id: string },
  b: { pick_bin?: number | null; tote_name?: string | null; customer?: string; order_id: string },
): number {
  const ba = binOf(a) ?? Number.MAX_SAFE_INTEGER
  const bb = binOf(b) ?? Number.MAX_SAFE_INTEGER
  return ba - bb || byCustomer(a, b)
}

const remainingOf = (l: FlavorPickLine) =>
  Math.max(0, Number(l.quantity_ordered ?? 0) - Number(l.quantity_picked ?? 0))

/** Lines of open orders (stored status open), including ones fully picked this session. */
/**
 * Still to pack: open orders, plus orders Zoho already closed (web store
 * orders are invoiced when placed) that aren't fully picked yet.
 */
export function isPackableStatus(status: string | null | undefined): boolean {
  const s = String(status ?? '').toLowerCase()
  return s === 'open' || s === 'closed'
}
const isOpen = (l: FlavorPickLine) => isPackableStatus(l.order_status)

/**
 * Open orders whose bag labels haven't all been printed yet. Same rule as the
 * Orders page "Printed" status: a line counts once it's printed (or already
 * picked, e.g. items added with UPDATEORDER).
 */
export function unprintedOrderIds(lines: FlavorPickLine[]): Set<string> {
  const ids = new Set<string>()
  for (const l of lines) {
    if (!isOpen(l)) continue
    const ordered = Number(l.quantity_ordered ?? 0)
    const covered = Math.max(Number(l.quantity_printed ?? 0), Number(l.quantity_picked ?? 0))
    if (covered < ordered) ids.add(String(l.order_id))
  }
  return ids
}

/**
 * Open, printed orders held back from picking: nothing picked yet and not
 * marked Ready to pick on the Orders page. Lets a new label run wait while
 * the last run's bags are still being packed.
 */
export function heldOrderIds(lines: FlavorPickLine[]): Set<string> {
  const unprinted = unprintedOrderIds(lines)
  const started = new Set<string>()
  const candidates = new Set<string>()
  for (const l of lines) {
    if (!isOpen(l)) continue
    const id = String(l.order_id)
    if (Number(l.quantity_picked ?? 0) > 0) started.add(id)
    if (!l.ready_to_pick_at && !unprinted.has(id)) candidates.add(id)
  }
  for (const id of started) candidates.delete(id)
  return candidates
}

/**
 * Lines of open, printed orders marked Ready to pick (including ones fully
 * picked this session). Orders that aren't printed yet have no bags to pick,
 * and held orders wait for Ready to pick, so both are left out.
 */
export function openOrderLines(lines: FlavorPickLine[]): FlavorPickLine[] {
  const unprinted = unprintedOrderIds(lines)
  const held = heldOrderIds(lines)
  return lines.filter(
    l => isOpen(l) && !unprinted.has(String(l.order_id)) && !held.has(String(l.order_id)),
  )
}

/** Lines of open orders that still need this SKU, by bin. */
export function linesNeedingSku(lines: FlavorPickLine[], sku: string): FlavorPickLine[] {
  return openOrderLines(lines)
    .filter(l => l.item_sku === sku && remainingOf(l) > 0)
    .sort(byBin)
}

/**
 * Orders on the pick screen still waiting for a bin number, in the order bins
 * should go out: by order number, oldest first (Jade, 2026-10-28). Totes
 * already full aren't given one.
 */
export function ordersNeedingBins(lines: FlavorPickLine[]): string[] {
  const firstLine = new Map<string, FlavorPickLine>()
  const left = new Set<string>()
  for (const l of openOrderLines(lines)) {
    const id = String(l.order_id)
    if (!firstLine.has(id)) firstLine.set(id, l)
    if (remainingOf(l) > 0) left.add(id)
  }
  return Array.from(firstLine.values())
    .filter(l => left.has(String(l.order_id)) && binOf(l) === null)
    .map(l => String(l.order_id))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

/** A tote's lines: still to pack first, then packed; A to Z within each. */
function sortToteLines(ls: FlavorPickLine[]): FlavorPickLine[] {
  return [...ls].sort(
    (a, b) =>
      Number(remainingOf(b) > 0) - Number(remainingOf(a) > 0) ||
      a.item_name.localeCompare(b.item_name),
  )
}

interface FlavorSummary {
  sku: string
  name: string
  bags: number
  orders: number
  /** Orders where this is the last flavor still missing. */
  finishes: number
}

function summarizeFlavors(lines: FlavorPickLine[]): FlavorSummary[] {
  const open = openOrderLines(lines)
  // Remaining SKUs per order, to find which flavor would finish an order.
  const skusLeftByOrder = new Map<string, Set<string>>()
  for (const l of open) {
    if (remainingOf(l) <= 0) continue
    const set = skusLeftByOrder.get(String(l.order_id)) ?? new Set<string>()
    set.add(l.item_sku)
    skusLeftByOrder.set(String(l.order_id), set)
  }
  const map = new Map<string, FlavorSummary & { orderIds: Set<string> }>()
  for (const l of open) {
    const left = remainingOf(l)
    if (left <= 0) continue
    const s = map.get(l.item_sku) ?? {
      sku: l.item_sku,
      name: l.item_name,
      bags: 0,
      orders: 0,
      finishes: 0,
      orderIds: new Set<string>(),
    }
    s.bags += left
    s.orderIds.add(String(l.order_id))
    map.set(l.item_sku, s)
  }
  const flavors = Array.from(map.values()).map(s => {
    let finishes = 0
    for (const id of s.orderIds) {
      if (skusLeftByOrder.get(id)?.size === 1) finishes++
    }
    return { sku: s.sku, name: s.name, bags: s.bags, orders: s.orderIds.size, finishes }
  })
  return groupSizes(flavors)
}

const SIZE_SUFFIX = /\s*-\s*(Small|Large)\s*$/i

/** Flavor name without its " - Small" / " - Large" suffix. */
function baseFlavorName(name: string): string {
  return name.replace(SIZE_SUFFIX, '').trim().toLowerCase()
}

/** Small first, then Large, then anything without a size. */
function sizeRank(name: string): number {
  const size = SIZE_SUFFIX.exec(name)?.[1]?.toLowerCase()
  return size === 'small' ? 0 : size === 'large' ? 1 : 2
}

/**
 * Keeps each flavor's Small and Large next to each other, since bags are
 * filled while picking. Flavors are ranked by their best size: finishes the
 * most totes, then most totes, then A to Z. Small comes before Large.
 */
function groupSizes(flavors: FlavorSummary[]): FlavorSummary[] {
  const groups = new Map<string, FlavorSummary[]>()
  for (const f of flavors) {
    const key = baseFlavorName(f.name)
    groups.set(key, [...(groups.get(key) ?? []), f])
  }
  return Array.from(groups.entries())
    .map(([key, items]) => ({
      key,
      finishes: Math.max(...items.map(f => f.finishes)),
      orders: Math.max(...items.map(f => f.orders)),
      items: items.sort(
        (a, b) => sizeRank(a.name) - sizeRank(b.name) || a.name.localeCompare(b.name),
      ),
    }))
    .sort(
      (a, b) => b.finishes - a.finishes || b.orders - a.orders || a.key.localeCompare(b.key),
    )
    .flatMap(g => g.items)
}

interface BoxSummary {
  order_id: string
  customer: string
  tote_name: string
  pick_bin: number | null
  ordered: number
  picked: number
  complete: boolean
  needsActive: number
}

function summarizeBoxes(
  lines: FlavorPickLine[],
  activeSku: string,
  finishedHere: Set<string>,
): BoxSummary[] {
  const map = new Map<string, BoxSummary>()
  for (const l of openOrderLines(lines)) {
    const key = String(l.order_id)
    const b = map.get(key) ?? {
      order_id: key,
      customer: toteName(l),
      tote_name: toteName(l),
      pick_bin: binOf(l),
      ordered: 0,
      picked: 0,
      complete: false,
      needsActive: 0,
    }
    b.ordered += Number(l.quantity_ordered ?? 0)
    b.picked += Math.min(Number(l.quantity_picked ?? 0), Number(l.quantity_ordered ?? 0))
    if (activeSku && l.item_sku === activeSku) b.needsActive += remainingOf(l)
    map.set(key, b)
  }
  return Array.from(map.values())
    .map(b => ({ ...b, complete: b.ordered > 0 && b.picked >= b.ordered }))
    // Totes already full when the page loaded aren't part of this pick run.
    .filter(b => !b.complete || finishedHere.has(b.order_id))
    .sort(byBin)
}

export default function FlavorPickPanel({
  lines,
  activeSku,
  loading,
  pickingIds,
  finishedHere,
  target,
  onClearTarget,
  onSelectFlavor,
  onClearFlavor,
  onPickRest,
  onPackInto,
  onToteDialogClosed,
  onPrintLabel,
  printingLabel = false,
  simple = false,
}: {
  lines: FlavorPickLine[]
  activeSku: string
  loading: boolean
  pickingIds: Set<number>
  /** Orders finished on this screen, kept as green totes until refresh. */
  finishedHere: Set<string>
  /** The tote (or order) bags are going into now. */
  target: { name: string; orderIds: Set<string>; bin?: number | null } | null
  onClearTarget: () => void
  onSelectFlavor: (sku: string) => void
  onClearFlavor: () => void
  onPickRest: (line: FlavorPickLine) => void
  /** "Pack into this tote" in a tote's flavor list: make it the target. */
  onPackInto: (line: FlavorPickLine) => void
  /** The tote's flavor list closed; the scan box should take scans again. */
  onToteDialogClosed: () => void
  /** Print a new tote label for the tote being packed (missing or damaged label). */
  onPrintLabel?: (() => void) | undefined
  printingLabel?: boolean
  /**
   * Scan Station: one screen for both ways of picking. Tapping a tote card
   * selects it, and the selected tote's flavors (packed and still to pack)
   * replace the flavor lists until it's full or cleared.
   */
  simple?: boolean
}) {
  // The tote (order) whose flavor list is open, from tapping its card.
  const [openToteId, setOpenToteId] = useState<string | null>(null)
  const toteLines = useMemo(
    () =>
      openToteId
        ? sortToteLines(openOrderLines(lines).filter(l => String(l.order_id) === openToteId))
        : [],
    [lines, openToteId],
  )
  // Simple mode: every line in the selected tote.
  const targetLines = useMemo(
    () =>
      simple && target
        ? sortToteLines(openOrderLines(lines).filter(l => target.orderIds.has(String(l.order_id))))
        : [],
    [simple, target, lines],
  )
  const targetOrdered = targetLines.reduce((sum, l) => sum + Number(l.quantity_ordered ?? 0), 0)
  const targetPacked = targetLines.reduce(
    (sum, l) => sum + Math.min(Number(l.quantity_picked ?? 0), Number(l.quantity_ordered ?? 0)),
    0,
  )
  const showTote = simple && !!target

  // One row per flavor in a tote: packed/ordered, green with a check when
  // done. withAll adds an "All" button to pack the rest of a flavor.
  const renderToteLines = (ls: FlavorPickLine[], withAll: boolean, maxH: string) => (
    <div className={cn('flex flex-col divide-y rounded-md border overflow-y-auto', maxH)}>
      {ls.map(l => {
        const ordered = Number(l.quantity_ordered ?? 0)
        const picked = Math.min(Number(l.quantity_picked ?? 0), ordered)
        const left = ordered - picked
        const done = left <= 0
        return (
          <div
            key={l.id}
            className={cn(
              'flex shrink-0 items-center gap-3 px-3 py-2',
              done
                ? 'bg-green-50 dark:bg-green-950/30'
                : activeSku && l.item_sku === activeSku && 'bg-primary/10',
            )}
          >
            {done ? (
              <Check className="w-5 h-5 text-green-600 shrink-0" aria-label="Packed" />
            ) : (
              <span className="w-5 shrink-0" />
            )}
            <span className="flex flex-col min-w-0">
              <span
                className={cn(
                  'text-base font-medium truncate',
                  done ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {l.item_name}
              </span>
              <span className="text-xs text-muted-foreground">{l.item_sku}</span>
            </span>
            <span className="ml-auto flex flex-col items-end shrink-0">
              <span className="text-xl font-bold text-foreground leading-none">
                {picked}/{ordered}
              </span>
              <span className="text-xs text-muted-foreground">
                {done ? 'packed' : `${left} to pack`}
              </span>
            </span>
            {withAll && !done && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                disabled={pickingIds.has(l.id)}
                onClick={() => onPickRest(l)}
                title={`Mark all ${left} as packed in this tote`}
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                All
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )
  const toteOrdered = toteLines.reduce((sum, l) => sum + Number(l.quantity_ordered ?? 0), 0)
  const totePacked = toteLines.reduce(
    (sum, l) => sum + Math.min(Number(l.quantity_picked ?? 0), Number(l.quantity_ordered ?? 0)),
    0,
  )
  const closeTote = () => {
    setOpenToteId(null)
    onToteDialogClosed()
  }
  const needing = useMemo(() => linesNeedingSku(lines, activeSku), [lines, activeSku])
  const flavors = useMemo(() => summarizeFlavors(lines), [lines])
  const boxes = useMemo(
    () => summarizeBoxes(lines, activeSku, finishedHere),
    [lines, activeSku, finishedHere],
  )
  const activeName =
    lines.find(l => l.item_sku === activeSku)?.item_name ?? activeSku
  const activeBags = needing.reduce((sum, l) => sum + remainingOf(l), 0)
  const boxesDone = boxes.filter(b => b.complete).length
  const unprintedCount = useMemo(() => unprintedOrderIds(lines).size, [lines])
  const heldCount = useMemo(() => heldOrderIds(lines).size, [lines])
  const bagsLeft = boxes.reduce((sum, b) => sum + Math.max(0, b.ordered - b.picked), 0)

  return (
    // Everything that says what to pick has to fit on one iPad screen, so the
    // lists scroll inside their own cards on wide screens instead of pushing
    // each other below the fold. Left: what you're doing now and what to pick
    // next. Right: every tote, by bin number.
    <div className="grid gap-3 lg:grid-cols-2 items-start">
      <div className="flex flex-col gap-3 min-w-0">
        {/* Tote being packed (only while one is selected) */}
        {target && (
          <div className="border border-green-600 bg-green-50 dark:bg-green-950/30 rounded-lg px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            {target.bin ? (
              <BinNumber bin={target.bin} className="h-12 min-w-12 text-3xl bg-green-600 text-white border-green-600" />
            ) : (
              <PackageOpen className="w-6 h-6 text-green-600 shrink-0" />
            )}
            <div className="flex flex-col min-w-0 flex-1 basis-40">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Packing into{target.bin ? ` bin ${target.bin}` : ''}
              </span>
              <span className="text-xl font-bold text-foreground leading-tight break-words line-clamp-2">
                {target.name}
              </span>
            </div>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              {onPrintLabel && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={printingLabel}
                  onClick={onPrintLabel}
                  title="Print a new bin label for this tote"
                >
                  <Printer className="w-4 h-4 mr-1" />
                  {printingLabel ? 'Printing…' : 'Bin Label'}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={onClearTarget}>
                <X className="w-4 h-4 mr-1" />
                Done with tote
              </Button>
            </div>
          </div>
        )}

        {/* Simple mode: the selected tote's flavors, packed and still to pack */}
        {showTote && (
          <div className="border rounded-lg p-3 bg-card flex flex-col gap-2">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-foreground">In this tote</h2>
              <span className="text-sm text-muted-foreground">
                {targetPacked}/{targetOrdered} bags packed · scan each bag as it goes in
              </span>
            </div>
            {targetLines.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Nothing left to pack.</p>
            ) : (
              renderToteLines(targetLines, true, 'max-h-[40vh] lg:max-h-[56vh]')
            )}
          </div>
        )}

        {/* Active flavor: which totes still need it (only once a flavor is picked) */}
        {activeSku && !showTote && (
          <div className="border rounded-lg p-3 bg-card flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-foreground">{activeName}</h2>
              <span className="text-sm text-muted-foreground">
                {needing.length === 0
                  ? 'all packed'
                  : `${activeBags} bag${activeBags !== 1 ? 's' : ''} for ${needing.length} tote${needing.length !== 1 ? 's' : ''}`}
              </span>
              <Button variant="ghost" size="sm" className="px-3 text-sm ml-auto" onClick={onClearFlavor}>
                <X className="w-3.5 h-3.5 mr-1" />
                Clear
              </Button>
            </div>
            {needing.length === 0 ? (
              <div className="flex items-center gap-2 rounded-md border border-green-600/40 bg-green-50 dark:bg-green-950/30 p-2 text-sm text-foreground">
                <PackageCheck className="w-5 h-5 text-green-600" />
                Every tote has its {activeName}. Scan the next flavor.
              </div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 lg:max-h-[34vh] lg:overflow-y-auto">
                {needing.map(l => {
                  const left = remainingOf(l)
                  const busy = pickingIds.has(l.id)
                  return (
                    <div
                      key={l.id}
                      className={cn(
                        'flex items-center gap-2 rounded-md border px-3 py-2',
                        target?.orderIds.has(String(l.order_id))
                          ? 'border-green-600 ring-2 ring-green-600 bg-green-50 dark:bg-green-950/30'
                          : 'border-primary/40 bg-primary/5',
                      )}
                    >
                      {/* Pickers look for the bin number first, so it's the biggest thing here. */}
                      <BinNumber bin={binOf(l)} className="h-16 min-w-16 px-2 text-5xl" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-semibold text-foreground truncate">
                          {toteName(l) || 'Unknown customer'}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {l.order_id} · {Number(l.quantity_picked)}/{Number(l.quantity_ordered)} in
                        </span>
                      </div>
                      <span className="ml-auto text-2xl font-bold text-foreground leading-none">
                        {left}
                      </span>
                      {left > 1 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          disabled={busy}
                          onClick={() => onPickRest(l)}
                          title={`Mark all ${left} as packed in this tote`}
                        >
                          <CheckCheck className="w-4 h-4 mr-1" />
                          All
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Next flavors: what to pick */}
        <div className={cn('border rounded-lg p-3 bg-card flex flex-col gap-2', showTote && 'hidden')}>
          <div className="flex items-baseline gap-2 flex-wrap">
            <h2 className="text-lg font-semibold text-foreground">Flavors to pick</h2>
            <span className="text-sm text-muted-foreground">
              {flavors.length} left · Small and Large together, ones that finish the most totes first
            </span>
          </div>
          {flavors.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              {loading ? 'Loading…' : 'Everything is picked.'}
            </p>
          ) : (
            <div className={cn('flex flex-col divide-y rounded-md border overflow-y-auto', activeSku || target ? 'max-h-[24vh] lg:max-h-[40vh]' : 'max-h-[30vh] lg:max-h-[62vh]')}>
              {flavors.map(f => (
                <button
                  key={f.sku}
                  type="button"
                  disabled={f.sku === activeSku}
                  onClick={() => onSelectFlavor(f.sku)}
                  className={cn(
                    'flex shrink-0 items-center gap-3 px-3 py-2 text-left min-h-[44px]',
                    f.sku === activeSku ? 'bg-primary/10' : 'hover:bg-muted',
                  )}
                >
                  <span className="flex flex-col min-w-0">
                    <span className="text-base font-medium text-foreground truncate">{f.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {f.orders} tote{f.orders !== 1 ? 's' : ''}
                      {f.finishes > 0 ? ` · finishes ${f.finishes}` : ''}
                    </span>
                  </span>
                  <span className="ml-auto flex flex-col items-end">
                    <span className="text-xl font-bold text-foreground leading-none">{f.bags}</span>
                    <span className="text-xs text-muted-foreground">bags</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Totes: one per open order, by bin number, green when complete */}
      <div className="border rounded-lg p-3 bg-card flex flex-col gap-2 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <h2 className="text-lg font-semibold text-foreground">Totes to pack</h2>
          <span className="text-sm text-muted-foreground">
            by bin · {boxesDone}/{boxes.length} done · {bagsLeft} bags left
          </span>
          {unprintedCount > 0 && (
            <span className="text-xs text-muted-foreground w-full">
              {unprintedCount} open order{unprintedCount !== 1 ? 's aren\'t' : ' isn\'t'} printed yet
              and {unprintedCount !== 1 ? 'are' : 'is'} left out.
            </span>
          )}
          {heldCount > 0 && (
            <span className="text-xs text-muted-foreground w-full">
              {heldCount} printed order{heldCount !== 1 ? 's are' : ' is'} waiting. Mark{' '}
              {heldCount !== 1 ? 'them' : 'it'} Ready to pick on the Orders page to pack here.
            </span>
          )}
        </div>
        {boxes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            {loading ? 'Loading…' : 'No open orders ready to pick.'}
          </p>
        ) : (
          // Columns adapt to the card's width (no squeezed cards), every row is
          // the same height, and the padding keeps the selected ring visible
          // inside the scroll area. The scroll limit sits on a wrapper: on the
          // grid itself, equal-height rows shrink to fit the limit and clip text.
          <div className="p-1 -m-1 lg:max-h-[70vh] lg:overflow-y-auto">
          <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] auto-rows-fr content-start">
            {boxes.map(b => {
              const left = Math.max(0, b.ordered - b.picked)
              return (
                <button
                  type="button"
                  key={b.order_id}
                  onClick={() => {
                    if (!simple) {
                      setOpenToteId(b.order_id)
                      return
                    }
                    // Simple mode: tap a tote to pack into it (tap again to let go).
                    if (target?.orderIds.has(b.order_id)) {
                      onClearTarget()
                      return
                    }
                    const first = lines.find(l => String(l.order_id) === b.order_id)
                    if (first) onPackInto(first)
                    onToteDialogClosed()
                  }}
                  aria-label={
                    (b.pick_bin ? `Bin ${b.pick_bin}: ` : '') +
                    (simple
                      ? `Pack into ${b.customer || 'this tote'}`
                      : `See ${b.customer || 'this tote'}'s flavors`)
                  }
                  className={cn(
                    'h-full min-h-[5.5rem] rounded-md border px-2.5 py-2 flex flex-col items-stretch gap-0.5 min-w-0 text-left [@media(hover:hover)]:hover:border-primary active:bg-accent transition-colors',
                    target?.orderIds.has(b.order_id) && 'ring-2 ring-green-600',
                    b.complete
                      ? 'border-green-600 bg-green-100 dark:bg-green-900/40'
                      : b.needsActive > 0
                        ? 'border-primary bg-primary/10'
                        : 'bg-background',
                  )}
                >
                  <span className="shrink-0 flex items-start gap-2 min-w-0">
                    <BinNumber
                      bin={b.pick_bin}
                      className={cn(
                        b.complete && 'bg-green-600 text-white border-green-600',
                        // A flavor is picked: the totes that need it show a big number.
                        !b.complete && b.needsActive > 0 &&
                          'h-14 min-w-14 text-4xl bg-primary text-primary-foreground border-primary',
                      )}
                    />
                    <span
                      className="min-w-0 text-sm font-semibold text-foreground break-words line-clamp-2 leading-snug"
                      title={b.customer || undefined}
                    >
                      {b.customer || 'Unknown customer'}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground truncate">
                    {b.order_id}
                  </span>
                  <span
                    className={cn(
                      'mt-auto pt-1 text-xs font-medium break-words leading-snug',
                      b.complete ? 'text-green-700 dark:text-green-300' : 'text-foreground',
                    )}
                  >
                    {b.complete
                      ? 'Complete, seal it'
                      : b.needsActive > 0
                        ? `needs ${b.needsActive} ${activeName} · ${left} left`
                        : `${left} bag${left !== 1 ? 's' : ''} left`}
                  </span>
                </button>
              )
            })}
          </div>
          </div>
        )}
      </div>

      {/* A tote's flavors: what's left to pack and what's already in it */}
      <Dialog open={openToteId !== null} onOpenChange={open => { if (!open) closeTote() }}>
        <DialogContent
          className="max-w-lg"
          onCloseAutoFocus={e => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>
              {lowestBin(toteLines) ? `Bin ${lowestBin(toteLines)} · ` : ''}
              {(toteLines[0] && toteName(toteLines[0])) || 'Tote'}
            </DialogTitle>
            <DialogDescription>
              {openToteId} · {totePacked}/{toteOrdered} bags packed
            </DialogDescription>
          </DialogHeader>
          {toteLines.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              This order isn't open anymore.
            </p>
          ) : (
            renderToteLines(toteLines, false, 'max-h-[55vh]')
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeTote}>
              Close
            </Button>
            {toteLines.length > 0 && totePacked < toteOrdered && (
              <Button
                onClick={() => {
                  const first = toteLines[0]
                  setOpenToteId(null)
                  if (first) onPackInto(first)
                  onToteDialogClosed()
                }}
              >
                <PackageOpen className="w-4 h-4 mr-1" />
                Pack into this tote
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** The tote's bin number, big enough to read from across the table. */
function BinNumber({ bin, className }: { bin: number | null | undefined; className?: string }) {
  if (!bin) return null
  return (
    <span
      className={cn(
        'shrink-0 inline-flex items-center justify-center rounded-md border-2 border-foreground/70 bg-background px-1.5 h-9 min-w-9 text-2xl font-extrabold leading-none tabular-nums text-foreground',
        className,
      )}
      aria-label={`Bin ${bin}`}
    >
      {bin}
    </span>
  )
}
